import React from 'react';
import Grid from '@mui/material/Grid';
import Typography from '@mui/material/Typography';
import Switch from '@mui/material/Switch';
import ModalButtons from 'src/components/UI/button/ModalButtons';
import { APICONSTANTS } from 'src/components/Constants';

import axios from 'src/axios';
import {
  Box,
  MenuItem,
  Select,
  TextField,
  IconButton,
  Tooltip,
  InputAdornment
} from '@mui/material';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';

import { FormControl } from '@mui/material';
import { SnackContext, AppContext, ThemeContext } from 'src/store/ContextStore';

import {
  getDependencies,
  getClinicalDefaults,
  saveClinicalDefault,
  deleteClinicalDefault,
  REFERRAL_TRIAGE
} from './formService';
import { meaningOf, offeredStatusLabels } from 'src/util/statusSemantics';
import TextInput from 'src/components/UI/input/TextInput';
import AutocompleteInput from 'src/components/UI/input/AutocompleteInput';
import UploadFileList from 'src/components/UI/input/UploadFileList';
import { useAttachmentChanges } from 'src/components/UI/input/attachments';
import ClinicalNoteDeleteModal from '../../diagnoses-menu/ClinicalNoteDeleteModal';
import FormLabel from 'src/components/UI/input/CustomFormLabel';
import DateInput from 'src/components/UI/input/DateInput';
import { calculateAgeOnDate } from 'src/util/util';
import { toLocalDateFromPickerOrImport } from 'src/util/partialDate';
import { useClinicalSuggestions } from 'src/hooks/useClinicalSuggestions';
import ClinicalSuggestionsPanel from 'src/components/UI/ClinicalSuggestionsPanel';
import { useAutoTranslation } from 'src/hooks/useTranslate';
import {
  ChangedField,
  useChangedFields
} from 'src/components/UI/input/changed-field';
import { SgAccessContext } from 'src/components/manage-users/SgAcessProvider';

// The fields an edit compares (TGCLOUD-1902), in screen order.
const REFERRAL_FIELDS = [
  'referralTo',
  'referralType',
  'referralDate',
  'referredBy',
  'ageAtRecord',
  'referralReason',
  'referralTriage',
  'referralStatus',
  'dateClosed',
  'reasonForClosure',
  'externalReferralId',
  'referralNotes',
  'fileAttachments',
  'showInFamily',
  'reminderEnabled',
  'reminderDate'
];

export default function ReferralForm({
  data,
  handleClose,
  familyMemberId,
  isProband = false,
  isEditing,
  model,
  isDisabled,
  // How many fields hold an unsaved change: what closing it asks by.
  onChangeCount
}) {
  const t = useAutoTranslation();

  const { theme } = React.useContext(ThemeContext);

  const { content } = React.useContext(AppContext);
  const { pedigreeSelectedUsers: users } = content;
  const { selectedUser: user } = users;

  // A new referral is where a pathway is allocated (TGCLOUD-1901): the live
  // pathways, for a caller whose security groups grant Pathways and a group
  // with the feature on. The server enrols on save and answers what it did.
  const { allow } = React.useContext(SgAccessContext) || {};
  const pathwaysOn =
    content?.settings?.systemFeatures?.FEATURE_PATHWAYS !== false &&
    (typeof allow === 'function' ? allow('pathways') : false);
  const [pathwayTemplates, setPathwayTemplates] = React.useState([]);
  const [pathwayTemplateId, setPathwayTemplateId] = React.useState('');
  React.useEffect(() => {
    if (!pathwaysOn || isEditing) return;
    axios
      .get(APICONSTANTS.pathwaysTemplates)
      .then(({ data: res }) => {
        const all = res?.templates || res || [];
        setPathwayTemplates(all.filter((tpl) => tpl.status === 'live'));
      })
      .catch(() => setPathwayTemplates([]));
  }, [pathwaysOn, isEditing]);

  const addDays = (date, days) => {
    const d = new Date(date || new Date());
    d.setDate(d.getDate() + days);
    return d;
  };

  const patientDob = data?.dob;
  // The age on the calendar day of the referral date (calculateAgeOnDate). The
  // date is handed over as stored — a picker instant, the importer's server new
  // Date() when the file has none, or UTC midnight from the spreadsheet
  // importer and demo seed — and only the raw value says which, so it is read
  // by toLocalDateFromPickerOrImport. `calculateAge` counted elapsed time from
  // a UTC-midnight birth date: a year short on the birthday at any positive
  // offset, and a positive age for a date before the birth, which is now blank.
  // The date field shows the day it read (asPickerDay), so the day on screen is
  // the day the age is from.
  const asPickerDay = toLocalDateFromPickerOrImport;
  const computeAgeAtRecord = React.useCallback(
    (dateValue) => calculateAgeOnDate(patientDob, dateValue),
    [patientDob]
  );

  const baseReferralDate = isEditing
    ? model?.referralDate || new Date()
    : new Date();

  const defaultReferralDate = new Date();
  const defaultValues = isEditing
    ? { ...model }
    : {
        familyId: user.familyId,
        familyMemberId: familyMemberId,
        referralTo: '',
        externalReferralId: '',
        referralDate: defaultReferralDate,
        referredBy: '',
        referralReason: '',
        referralTriage: '',
        referralStatus: 'Open', // Open, Closed
        referralType: 'None',
        fileAttachments: [], // Make this form
        referralNotes: '',
        showInFamily: false,
        dateClosed: null,
        reasonForClosure: '',
        reminderEnabled: false,
        reminderDate: addDays(baseReferralDate, 14),
        ageAtRecord: computeAgeAtRecord(defaultReferralDate)
      };
  const [formValues, setFormValues] = React.useState(defaultValues);
  const ageIsComputed = !!patientDob && !!formValues.referralDate;
  // Unsaved changes (TGCLOUD-1902): each field against the record as opened.
  const changes = useChangedFields({
    baseline: defaultValues,
    values: formValues,
    mode: isEditing ? 'edit' : 'create',
    fields: REFERRAL_FIELDS,
    exclude: (name) => name === 'ageAtRecord' && ageIsComputed
  });
  React.useEffect(() => {
    onChangeCount?.(changes.unsavedCount);
  }, [changes.unsavedCount]); // eslint-disable-line react-hooks/exhaustive-deps
  // Attachment deletes wait for a successful save (TGCLOUD-1850).
  const attachmentChanges = useAttachmentChanges(defaultValues.fileAttachments);
  const [uploadingFiles, setUploadingFiles] = React.useState(false);
  const [reminderTouched, setReminderTouched] = React.useState(false);

  const [typeOptions, setTypeOptions] = React.useState([]);
  const [reasonOptions, setReasonOptions] = React.useState([]);
  const [referralStatusOptions, setReferralStatusOptions] = React.useState([]);
  const [open, setOpen] = React.useState(false);
  const [loading, setLoading] = React.useState(false);

  const [error, setError] = React.useState({});

  React.useEffect(() => {
    getClinicalDefaults('referralTypes').then(setTypeOptions);
    getClinicalDefaults('referralReasons').then(setReasonOptions);
    getClinicalDefaults('referralStatuses').then(setReferralStatusOptions);
  }, []);

  React.useEffect(() => {
    if (!patientDob) return;
    const nextAge = computeAgeAtRecord(formValues.referralDate);
    setFormValues((prev) => {
      const currentAge = prev.ageAtRecord === undefined ? '' : prev.ageAtRecord;
      if (nextAge === '' && currentAge === '') return prev;
      if (nextAge === currentAge) return prev;
      return { ...prev, ageAtRecord: nextAge };
    });
  }, [patientDob, computeAgeAtRecord, formValues.referralDate]);

  React.useEffect(() => {
    if (!formValues.referralDate) return;
    if (formValues.reminderEnabled && !reminderTouched) {
      setFormValues((prev) => ({
        ...prev,
        reminderDate: addDays(formValues.referralDate, 14)
      }));
    }
  }, [formValues.referralDate]);

  const handleChange = (e) => {
    if (e.target.name === 'referralDate') {
      const nextDate = e.target.value;
      setFormValues((prevValues) => ({
        ...prevValues,
        ageAtRecord: computeAgeAtRecord(nextDate)
      }));
    }

    if (e.target.type === 'date') {
      const selectedDate = new Date(e.target.value);
      const today = new Date();

      if (selectedDate >= today) {
        setError((prev) => ({ ...prev, [e.target.name]: true }));
      } else setError({});
    }

    setFormValues((prevValues) => ({
      ...prevValues,
      [e.target.name]: e.target.value
    }));
  };

  const { setSnack } = React.useContext(SnackContext);

  const clinicalSuggestions = useClinicalSuggestions({
    familyMemberId,
    isProband
  });
  const {
    analyzingNote,
    handleAttachmentText,
    analyzeText,
    acceptSuggestions
  } = clinicalSuggestions;

  //Sure about this! Will be good for show in Family
  const onToggleSwitch = (e) => {
    setFormValues((prevValues) => ({
      ...prevValues,
      [e.target.name]: e.target.checked
    }));
    if (familyMemberId == 'none') {
      setFormValues((prevValues) => ({
        ...prevValues,
        showInFamily: true
      }));
    }
  };

  const handleCloseModal = () => {
    setOpen(false);
  };
  const handleOpenModal = () => {
    setOpen(true);
  };

  const deleteNote = async () => {
    setLoading(true);

    const dep = await getDependencies(
      user.familyId,
      model._id,
      theme.dateFormat,
      t
    );
    if (dep.length !== 0) {
      setSnack({
        status: true,
        msg: t('Associated record exists; check the {{value}}', {
          value: dep[0]
        }),
        severity: 'error'
      });
      setLoading(false);

      return;
    }
    axios
      .delete(`/clinical-notes/note`, {
        params: {
          _id: model._id
        }
      })
      .then(() => {
        setSnack({
          status: true,
          msg: t('Record deleted'),
          severity: 'success'
        });
        handleCloseModal();

        handleClose({ exit: true });
      })
      .catch((err) => {
        setSnack({
          status: true,
          msg: t('Delete failed! {{err}}', { err }),
          severity: 'error'
        });
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const validateForm = (values) => {
    let errors = {};

    if (values.referralDate === '') {
      errors.referralDate = t('Referral Date is required');
    }
    if (values.referredBy === '') {
      errors.referredBy = t('Referred By is required');
    }

    if (values.referralDate) {
      // It read `values.dateTreferralDateaken`, a field that does not exist,
      // so the check never fired (TGCLOUD-1901, found in passing).
      const selectedDate = new Date(values.referralDate);
      const today = new Date();

      if (selectedDate > today) {
        errors.referralDate = t('Referral date cannot be in the future');
      }
    }

    setError(errors);
    return errors;
  };

  //Think about this
  const handleSubmit = (e) => {
    e.preventDefault();
    e.stopPropagation();
    const errors = validateForm(formValues);

    if (Object.keys(errors).length > 0) return Promise.reject();

    const payload = {
      ...formValues,
      reminderEnabled: formValues.reminderEnabled,
      reminderDate: formValues.reminderEnabled ? formValues.reminderDate : null,
      ...(!isEditing && pathwayTemplateId ? { pathwayTemplateId } : {})
    };

    return axios
      .put(APICONSTANTS.referral, payload)
      .then((res) => {
        handleClose({ exit: true });
        if (res.status === 200) {
          attachmentChanges.commit(payload.fileAttachments, familyMemberId);
          const pathway = res.data?.pathway;
          const chosen = pathwayTemplates.find(
            (tpl) => String(tpl._id) === String(pathwayTemplateId)
          );
          if (pathway && pathway.enrolled?.length && chosen) {
            setSnack({
              status: true,
              msg: t('Referral saved and the patient assigned to {{name}}', {
                name: chosen.name
              }),
              severity: 'success'
            });
          } else if (pathway && pathway.skipped?.length) {
            setSnack({
              status: true,
              msg: t(
                'Referral saved, but not assigned to the pathway: {{reason}}',
                { reason: pathway.skipped[0].reason }
              ),
              severity: 'warning'
            });
          } else {
            setSnack({
              status: true,
              msg: t('Successfully saved'),
              severity: 'success'
            });
          }
        } else {
          setSnack({
            status: true,
            msg: t('Upload failed!'),
            severity: 'error'
          });
        }
      })
      .catch((err) => {
        setSnack({
          status: true,
          msg: t('Upload failed! {{err}}', { err }),
          severity: 'error'
        });
      });
  };

  return (
    //Should be fine to reuse old class?

    <form className="user-form">
      <ClinicalNoteDeleteModal
        loading={loading}
        open={open}
        handleCloseModal={handleCloseModal}
        onConfirm={deleteNote}
        title="Are you sure you want to delete this Referral ?"
        content={t(
          'Warning: This action will permanently delete this Referral. Once deleted, this information will be permanently removed and cannot be restored.'
        )}
      />
      <Grid container spacing={2}>
        <ChangedField
          component={Grid}
          size={12}
          name="referralTo"
          changed={changes.changed.referralTo}
        >
          <TextInput
            label="Referral To"
            variant="outlined"
            fullWidth
            onChange={handleChange}
            name="referralTo"
            value={formValues.referralTo}
            InputProps={{ readOnly: isDisabled }}
          />
        </ChangedField>

        <ChangedField
          component={Grid}
          size={6}
          name="referralType"
          changed={changes.changed.referralType}
        >
          <Grid sx={{ paddingBlock: 0.25 }}>
            <FormLabel>Referral Type</FormLabel>
          </Grid>
          <AutocompleteInput
            name="referralType"
            value={formValues.referralType || ''}
            onChange={(newValue) => {
              const label = newValue ? newValue.label || newValue : '';
              handleChange({ target: { name: 'referralType', value: label } });
              if (label) {
                saveClinicalDefault('referralTypes', label).then(
                  setTypeOptions
                );
              }
            }}
            options={typeOptions}
            onDelete={(option) => {
              deleteClinicalDefault('referralTypes', option.label).then(
                setTypeOptions
              );
            }}
            helperText={t('Please select or enter a referral type')}
            disabled={isDisabled}
          />
        </ChangedField>

        <ChangedField
          component={Grid}
          size={6}
          name="referralDate"
          changed={changes.changed.referralDate}
        >
          <Grid sx={{ paddingBlock: 0.25 }}>
            <FormLabel required>Referral Date</FormLabel>
          </Grid>
          <DateInput
            value={asPickerDay(formValues.referralDate)}
            liveText
            disableFuture
            onChange={(val) =>
              handleChange({
                target: { name: 'referralDate', value: val, type: 'date' }
              })
            }
            variant="outline"
            error={error?.referralDate}
            helperText={t('Date cannot be in the future')}
            readOnly={isDisabled}
          />
        </ChangedField>

        <ChangedField
          component={Grid}
          size={6}
          name="referredBy"
          changed={changes.changed.referredBy}
        >
          <TextInput
            label="Referred By"
            variant="outlined"
            fullWidth
            onChange={handleChange}
            name="referredBy"
            value={formValues.referredBy}
            required={true}
            error={!!error.referredBy}
            helperText={error.referredBy}
            InputProps={{ readOnly: isDisabled }}
          />
        </ChangedField>
        <ChangedField
          component={Grid}
          size={6}
          name="ageAtRecord"
          changed={changes.changed.ageAtRecord}
          disabled={ageIsComputed}
        >
          <TextInput
            label="Age at Referral"
            name="ageAtRecord"
            type="number"
            value={formValues.ageAtRecord ?? ''}
            inputProps={{ min: 0, className: 'no-spinners' }}
            // Disabled (muted) only when an age is actually being
            // auto-calculated, i.e. DoB AND referral date both set.
            // Editable when either is missing so a known age can be
            // recorded directly.
            disabled={!!patientDob && !!formValues.referralDate}
            onChange={
              patientDob && formValues.referralDate
                ? undefined
                : (e) => {
                    const val = e.target.value;
                    const parsed = val === '' ? '' : Number(val);
                    if (val === '' || !Number.isNaN(parsed)) {
                      setFormValues((prev) => ({
                        ...prev,
                        ageAtRecord: val === '' ? '' : parsed
                      }));
                    }
                  }
            }
          />
        </ChangedField>

        <ChangedField
          component={Grid}
          size={6}
          name="referralReason"
          changed={changes.changed.referralReason}
        >
          <TextInput
            label="Referral Reason"
            variant="outlined"
            fullWidth
            type="autocomplete"
            options={reasonOptions || []}
            onChange={(e) => {
              const label = e ? e.label : '';
              handleChange({
                target: { name: 'referralReason', value: label }
              });
              // Persist the value as well as using it, so it is offered on
              // the next record. This picker has always shown privileged roles
              // an Add row; until now the value it produced was written to the
              // record and then lost, so the same clinician had to retype it
              // every time (TGCLOUD-1784).
              if (label) {
                saveClinicalDefault('referralReasons', label).then(
                  setReasonOptions
                );
              }
            }}
            onDelete={(option) => {
              deleteClinicalDefault('referralReasons', option.label).then(
                setReasonOptions
              );
            }}
            name="referralReason"
            value={formValues.referralReason}
            readOnly={isDisabled}
          />
        </ChangedField>

        <ChangedField
          component={Grid}
          size={6}
          name="referralTriage"
          changed={changes.changed.referralTriage}
        >
          <TextInput
            label="Referral Triage"
            variant="outlined"
            fullWidth
            type="select"
            options={REFERRAL_TRIAGE}
            onChange={handleChange}
            name="referralTriage"
            value={formValues.referralTriage}
            disabled={isDisabled}
          />
        </ChangedField>

        <ChangedField
          component={Grid}
          size={6}
          name="referralStatus"
          changed={changes.changed.referralStatus}
        >
          <Grid sx={{ paddingBlock: 0.5 }}>
            <FormLabel>Referral Status</FormLabel>
          </Grid>
          <Box sx={{ display: 'flex', position: 'relative' }}>
            <FormControl fullWidth>
              <Select
                style={{ width: '100%' }}
                name="referralStatus"
                value={formValues.referralStatus}
                onChange={handleChange}
                readOnly={isDisabled}
              >
                {/* The configured list, with the record's own value added
                    when the clinic no longer offers it, so a status written by
                    an import or retired by an administrator can still be saved
                    (TGCLOUD-1784). Falls back to the wording this product has
                    always written until the list loads. */}
                {offeredStatusLabels(
                  referralStatusOptions,
                  formValues.referralStatus,
                  'referralStatuses'
                ).map((option) => (
                  <MenuItem value={option} key={option}>
                    {option}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
        </ChangedField>

        {/* The closure fields appear for any value that MEANS closed, not
            only for the word (TGCLOUD-1784). */}
        {meaningOf('referralStatuses', formValues.referralStatus) ===
          'closed' && (
          <>
            <ChangedField
              component={Grid}
              size={6}
              name="dateClosed"
              changed={changes.changed.dateClosed}
            >
              <Grid sx={{ paddingBlock: 0.5 }}>
                <FormLabel>Date Closed</FormLabel>
              </Grid>
              <DateInput
                name="dateClosed"
                value={formValues.dateClosed}
                onChange={(e) =>
                  setFormValues((prevValues) => ({
                    ...prevValues,
                    dateClosed: e
                  }))
                }
                variant="outline"
                minDate={
                  isEditing
                    ? new Date(1600, 0, 0)
                    : asPickerDay(formValues.referralDate)
                }
                readOnly={isDisabled}
              />
            </ChangedField>

            <ChangedField
              component={Grid}
              size={12}
              name="reasonForClosure"
              changed={changes.changed.reasonForClosure}
            >
              <TextInput
                label="Reason For Closure"
                variant="outlined"
                fullWidth
                onChange={handleChange}
                name="reasonForClosure"
                value={formValues.reasonForClosure}
                multiline
                InputProps={{ readOnly: isDisabled }}
              />
            </ChangedField>
          </>
        )}

        <ChangedField
          component={Grid}
          size={6}
          name="externalReferralId"
          changed={changes.changed.externalReferralId}
        >
          <TextInput
            label="External Referral ID"
            variant="outlined"
            fullWidth
            onChange={handleChange}
            name="externalReferralId"
            value={formValues.externalReferralId}
            InputProps={{ readOnly: isDisabled }}
          />
        </ChangedField>

        <ChangedField
          component={Grid}
          size={12}
          name="referralNotes"
          changed={changes.changed.referralNotes}
        >
          <TextInput
            label="Referral Notes"
            variant="outlined"
            fullWidth
            onChange={handleChange}
            name="referralNotes"
            value={formValues.referralNotes}
            multiline
            InputProps={{
              readOnly: isDisabled,
              endAdornment: formValues.referralNotes ? (
                <InputAdornment position="end" sx={{ alignSelf: 'center' }}>
                  <Tooltip title={t('Analyse for clinical terms')}>
                    <span style={{ lineHeight: 0 }}>
                      <IconButton
                        size="medium"
                        onClick={() =>
                          analyzeText(formValues.referralNotes, 'manual')
                        }
                        disabled={analyzingNote || isDisabled}
                      >
                        <AutoAwesomeIcon />
                      </IconButton>
                    </span>
                  </Tooltip>
                </InputAdornment>
              ) : null
            }}
          />
        </ChangedField>

        <ClinicalSuggestionsPanel
          state={clinicalSuggestions}
          onAccept={() =>
            acceptSuggestions().then((result) => {
              if (result?.success) {
                const { count = {} } = result;
                const parts = [];
                if (count.phenotypes)
                  parts.push(`${count.phenotypes} phenotype(s)`);
                if (count.genotypes)
                  parts.push(`${count.genotypes} genotype(s)`);
                if (count.diseases) parts.push(`${count.diseases} disease(s)`);
                setSnack({
                  status: true,
                  msg: t('Added {{parts}} to the record', {
                    parts: parts.join(', ')
                  }),
                  severity: 'success'
                });
              } else {
                setSnack({
                  status: true,
                  msg: t('Failed to save suggestions'),
                  severity: 'error'
                });
              }
            })
          }
          disabled={isDisabled}
        />

        <UploadFileList
          changed={changes.changed.fileAttachments}
          value={formValues.fileAttachments}
          familyId={familyMemberId}
          onChange={(next) => {
            // Functional: uploads can finish faster than this form re-renders.
            setFormValues((prev) => ({ ...prev, fileAttachments: next }));
          }}
          readOnly={isDisabled}
          onBusyChange={setUploadingFiles}
          onUploadComplete={(uploadData, name) => {
            attachmentChanges.trackUpload(name);
            const text = uploadData?.extractedText;
            if (text) handleAttachmentText(text, name);
          }}
        />

        <ChangedField
          component={Grid}
          container
          size={12}
          sx={{ alignItems: 'center' }}
          name="showInFamily"
          changed={changes.changed.showInFamily}
          variant="control"
          kind="flag"
        >
          <Grid
            size={{
              xs: 6,
              sm: 7
            }}
          >
            <FormLabel component="label" className="form-label">
              Show in family
            </FormLabel>
          </Grid>
          <Grid
            container
            size={{
              xs: 6,
              sm: 3
            }}
            sx={{
              alignItems: 'center'
            }}
          >
            <Grid>
              <Switch
                color="secondary"
                checked={formValues.showInFamily ?? false}
                onChange={onToggleSwitch}
                name="showInFamily"
                disabled={isDisabled}
              />
            </Grid>
            <Grid>
              <Typography>
                {formValues.showInFamily ? t('Yes') : t('No')}
              </Typography>
            </Grid>
          </Grid>
        </ChangedField>

        <Grid size={12}>
          <Grid
            container
            spacing={2}
            sx={{
              alignItems: 'center'
            }}
          >
            <Grid
              size={{
                xs: 12,
                sm: 'auto'
              }}
            >
              <FormLabel>Reminder</FormLabel>
            </Grid>
            <ChangedField
              component={Grid}
              name="reminderEnabled"
              changed={changes.changed.reminderEnabled}
              variant="control"
              kind="flag"
            >
              <Switch
                color="secondary"
                checked={formValues.reminderEnabled ?? false}
                onChange={(e) => {
                  const enabled = e.target.checked;
                  setFormValues((prev) => ({
                    ...prev,
                    reminderEnabled: enabled,
                    // when enabling, default to referralDate + 14 days (or today + 14 if none)
                    reminderDate: enabled
                      ? addDays(prev.referralDate || new Date(), 14)
                      : null
                  }));
                  if (!enabled) setReminderTouched(false);
                }}
                name="reminderEnabled"
                disabled={isDisabled}
              />
            </ChangedField>

            {formValues.reminderEnabled && (
              <ChangedField
                component={Grid}
                sx={{ marginLeft: 14 }}
                size={{ xs: 12, sm: 6 }}
                name="reminderDate"
                changed={changes.changed.reminderDate}
              >
                <FormLabel>Reminder Date</FormLabel>
                <DateInput
                  name="reminderDate"
                  value={formValues.reminderDate || addDays(new Date(), 14)}
                  onChange={(val) => {
                    setReminderTouched(true); // user changed it manually
                    setFormValues((prev) => ({ ...prev, reminderDate: val }));
                  }}
                  variant="outline"
                  minDate={asPickerDay(formValues.referralDate) || new Date()}
                />
              </ChangedField>
            )}
          </Grid>
        </Grid>

        {pathwaysOn && !isEditing && pathwayTemplates.length > 0 && (
          <Grid size={12}>
            <FormLabel component="label" className="form-label">
              Assign to a pathway
            </FormLabel>
            <TextInput
              type="select"
              name="pathwayTemplateId"
              value={pathwayTemplateId}
              onChange={(e) => setPathwayTemplateId(e.target.value)}
              translateOptions={false}
              options={[
                { label: t('— Not now —'), value: '' },
                ...pathwayTemplates.map((tpl) => ({
                  label: tpl.name,
                  value: tpl._id
                }))
              ]}
              helperText={t(
                'The patient is placed on the pathway when the referral is saved; its first stage starts at once.'
              )}
            />
          </Grid>
        )}

        <ModalButtons
          hideSaveButton={isDisabled}
          isEditing={isEditing}
          hideContinueAdding={true}
          onClick={handleSubmit}
          handleClose={handleClose}
          intermediateBtnLabel="Delete"
          intermediateAction={isEditing && !isDisabled ? handleOpenModal : null}
          addLabel="Save New Referral"
          saveButtonProps={
            uploadingFiles
              ? {
                  disabled: true,
                  title: 'Waiting for files to finish uploading'
                }
              : undefined
          }
          unsavedCount={changes.count}
        />
      </Grid>
    </form>
  );
}
